import React from 'react';
import I18n from 'i18n-js';
import { gql } from '@apollo/client';

import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { RCButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { Listing } from '@reverbdotcom/commons/src/gql/graphql';
import { TrashIcon } from '@reverbdotcom/cadence/icons/react';
import { ModalDialog } from '@reverbdotcom/commons';
import { isEmpty } from 'lodash';
import { useIsMobileWindowWidth } from '@reverbdotcom/cadence/components/helpers/is-mobile';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

export const COMPONENT_NAME = 'BulkDeleteButton';
export const DELETABLE_STATES = [
  'draft',
];
interface IExternalProps {
  selectedListings: Listing[];
  onSuccessCallback: (listingResponses) => void;
  onErrorCallback: (errorMessage: string) => void;
}

const BULK_DELETE_MUTATION = gql`
  mutation Core_MyListings_BulkDelete(
    $input: Input_core_apimessages_BulkDeleteListingsRequest
  ) {
    bulkDeleteListings(input: $input) {
      listings {
        listingId
        success
        deleteErrors {
          message
        }
      }
    }
  }
`;

export default function BulkDeleteButton({
  selectedListings,
  onSuccessCallback,
  onErrorCallback,
}: IExternalProps) {
  const isMobile = useIsMobileWindowWidth();

  const listingIds = selectedListings.map(listing => listing.id);

  const deletableListings = selectedListings.filter(listing => DELETABLE_STATES.includes(listing.state));
  const undeletableListings = selectedListings.filter(listing => !DELETABLE_STATES.includes(listing.state));

  const hasDeletableListings = !!deletableListings.length;
  const hasNoDeletableListings = undeletableListings.length === selectedListings.length;
  const hasUndeletableListings = !!undeletableListings.length;

  const modalSubmitI18nKey = hasNoDeletableListings ? 'close' : 'delete';

  const [isErrorModalOpen, showErrorModal] = React.useState(false);

  const [bulkDeleteListings, {
    data = {},
    loading,
    errors,
  }] = useMutation(BULK_DELETE_MUTATION);

  const { listings } = data.bulkDeleteListings ?? {};

  const hasErrors = !!errors.length;

  React.useEffect(() => {
    if (loading) return;

    if (hasErrors) return handleUnexpectedErrors();

    handleResponse();
  }, [loading]);

  function handleResponse() {
    if (isEmpty(data)) return;

    onSuccessCallback(listings);
  }

  function handleUnexpectedErrors() {
    onErrorCallback(
      I18n.t('discovery.listingsManagement.genericError'),
    );
  }

  function deleteListings() {
    const deletableListingIds = deletableListings.map(listing => listing.id);

    trackEvent({
      componentName: COMPONENT_NAME,
      eventName: MParticleEventName.DeletingListings,
      listingIds: deletableListingIds,
    });

    bulkDeleteListings({
      variables: {
        input: {
          listingIds: deletableListingIds,
        },
      },
    });
  }

  function handleDefaultSubmit() {
    trackEvent({
      eventName: MParticleEventName.ClickedComponent,
      componentName: COMPONENT_NAME,
    });

    if (hasUndeletableListings) {
      showErrorModal(true);
    } else {
      deleteListings();
    }
  }

  function handleModalSubmit() {
    if (hasNoDeletableListings) {
      showErrorModal(false);
    } else {
      showErrorModal(false);
      deleteListings();
    }
  }

  useViewTracking({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
  }, true);

  return (
    <>
      <RCButton
        fullWidth
        variant="outlined"
        size={isMobile ? 'medium' : 'mini'}
        onClick={handleDefaultSubmit}
        disabled={!listingIds.length || loading}
      >
        <RCTextWithIcon svgComponent={TrashIcon} placement="right">
          {I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkDelete.deleteSelected')}
        </RCTextWithIcon>
      </RCButton>
      <ModalDialog
        isOpen={isErrorModalOpen}
        headerTitle={I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkDelete.deleteErrorModal.headerTitle', {
          count: deletableListings.length,
        })}
        saveButtonText={I18n.t(`discovery.listingsManagement.toolbar.bulkActions.bulkDelete.deleteErrorModal.${modalSubmitI18nKey}`)}
        hideFooterDismissButton={hasNoDeletableListings}
        onSubmit={handleModalSubmit}
      >
        {hasDeletableListings && (
          <div className="weight-bold mb-4">
            {I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkDelete.deleteErrorModal.deleteMessage', {
              count: deletableListings.length,
            })}
          </div>
        )}

        <div className="weight-bold">
          {I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkDelete.deleteErrorModal.errorMessage', {
            count: undeletableListings.length,
          })}
        </div>
      </ModalDialog>
    </>
  );
}
